import React, { useState } from 'react';
import { Firestore } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { Database, ArrowLeft, Wand2, GitBranch } from 'lucide-react';
import MediaTypeSelector from './components/MediaTypeSelector';
import TableSelector from './components/TableSelector';
import ColumnMapper from './components/ColumnMapper';
import PromptBuilder from './components/PromptBuilder';
import PatternBuilder from './components/PatternBuilder';
import PreviewPanel from './components/PreviewPanel';
import PatternPreviewPanel from './components/PatternPreviewPanel';
import { PatternRule, PatternStep } from 'services/apiService';

interface BulkTreatmentProps {
  db: Firestore;
  uid: string;
}

interface BigQueryConfig {
  project_id: string;
  dataset_id: string;
  table_name: string;
}

interface TableSchema {
  columns: Array<{
    name: string;
    type: string;
  }>;
}

type TreatmentMode = 'prompt' | 'pattern';

const BulkTreatment: React.FC<BulkTreatmentProps> = ({ db, uid }) => {
  const navigate = useNavigate();
  const { project_id } = useParams<{ project_id: string }>();
  
  const [step, setStep] = useState<'media-type' | 'select' | 'mode' | 'configure' | 'preview'>('media-type');
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [treatmentMode, setTreatmentMode] = useState<TreatmentMode>('prompt');
  const [bqConfig, setBqConfig] = useState<BigQueryConfig | null>(null);
  const [tableSchema, setTableSchema] = useState<TableSchema | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [imageColumn, setImageColumn] = useState<string | null>(null);
  const [promptRules, setPromptRules] = useState<PromptRule[]>([]);
  const [patternRules, setPatternRules] = useState<PatternRule[]>([]);
  const [patternPreviews, setPatternPreviews] = useState<Record<string, PatternStep[]>>({});
  const [treatmentName, setTreatmentName] = useState<string>('');
  const [removeBackground, setRemoveBackground] = useState<boolean>(false);

  const handleMediaTypeSelected = (type: MediaType) => {
    setMediaType(type);
    setStep('select');
  };

  const handleTableSelected = (config: BigQueryConfig, schema: TableSchema) => {
    setBqConfig(config);
    setTableSchema(schema);
    setStep('mode');
  };

  const handleModeSelected = (mode: TreatmentMode) => {
    setTreatmentMode(mode);
    setStep('configure');
  };

  const handleConfigurationComplete = () => {
    setStep('preview');
  };

  const handleBack = () => {
    if (step === 'preview') {
      setStep('configure');
    } else if (step === 'configure') {
      setStep('mode');
    } else if (step === 'mode') {
      setStep('select');
    } else if (step === 'select') {
      setStep('media-type');
      setMediaType(null);
    } else {
      navigate(`/projects/${project_id}/bulk-treatments`);
    }
  };

  if (!project_id) {
    navigate('/');
    return null;
  }

  return (
    <div className="text-gray-100 flex flex-col">
      
      <main className="flex-grow w-full max-w-7xl mx-auto p-4 md:p-8">
        {/* Header Section */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl border border-blue-500/30">
              <Database className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Bulk Treatment
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Process input images from BigQuery and generate {mediaType === 'image' ? 'new images' : mediaType === 'video' ? 'videos' : 'media'} with AI
              </p>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-4 mt-6">
            <StepIndicator 
              number={1} 
              label="Media Type" 
              active={step === 'media-type'} 
              completed={step !== 'media-type'} 
            />
            <div className={`flex-1 h-0.5 ${step !== 'media-type' ? 'bg-blue-500' : 'bg-gray-700'}`} />
            <StepIndicator 
              number={2} 
              label="Select Table" 
              active={step === 'select'} 
              completed={['mode', 'configure', 'preview'].includes(step)} 
            />
            <div className={`flex-1 h-0.5 ${['mode', 'configure', 'preview'].includes(step) ? 'bg-blue-500' : 'bg-gray-700'}`} />
            <StepIndicator 
              number={3} 
              label="Mode" 
              active={step === 'mode'} 
              completed={['configure', 'preview'].includes(step)} 
            />
            <div className={`flex-1 h-0.5 ${['configure', 'preview'].includes(step) ? 'bg-blue-500' : 'bg-gray-700'}`} />
            <StepIndicator 
              number={4} 
              label="Configure" 
              active={step === 'configure'} 
              completed={step === 'preview'} 
            />
            <div className={`flex-1 h-0.5 ${step === 'preview' ? 'bg-blue-500' : 'bg-gray-700'}`} />
            <StepIndicator 
              number={5} 
              label="Preview & Launch" 
              active={step === 'preview'} 
              completed={false} 
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 shadow-2xl">
          {step === 'media-type' && (
            <MediaTypeSelector onMediaTypeSelected={handleMediaTypeSelected} />
          )}

          {step === 'select' && (
            <TableSelector 
              onTableSelected={handleTableSelected} 
              firestoreProjectId={project_id}
              existingConfig={bqConfig}
            />
          )}

          {step === 'mode' && (
            <TreatmentModeSelector onModeSelected={handleModeSelected} />
          )}

          {step === 'configure' && bqConfig && tableSchema && mediaType && (
            <div className="space-y-6">
              <ColumnMapper 
                schema={tableSchema}
                selectedColumns={selectedColumns}
                onColumnsChange={setSelectedColumns}
                imageColumn={imageColumn}
                onImageColumnChange={setImageColumn}
                mediaType={mediaType}
              />
              
              {treatmentMode === 'prompt' ? (
                <PromptBuilder
                  selectedColumns={selectedColumns}
                  promptRules={promptRules}
                  onPromptRulesChange={setPromptRules}
                  treatmentName={treatmentName}
                  onNameChange={setTreatmentName}
                  imageColumn={imageColumn}
                  onNext={handleConfigurationComplete}
                  mediaType={mediaType}
                  removeBackground={removeBackground}
                  onRemoveBackgroundChange={setRemoveBackground}
                />
              ) : (
                <PatternBuilder
                  selectedColumns={selectedColumns}
                  patternRules={patternRules}
                  onPatternRulesChange={setPatternRules}
                  patternPreviews={patternPreviews}
                  onPatternPreviewsChange={setPatternPreviews}
                  treatmentName={treatmentName}
                  onNameChange={setTreatmentName}
                  imageColumn={imageColumn}
                  onNext={handleConfigurationComplete}
                  projectId={project_id}
                  db={db}
                  uid={uid}
                />
              )}
            </div>
          )}

          {step === 'preview' && bqConfig && project_id && mediaType && (
            treatmentMode === 'prompt' ? (
              <PreviewPanel
                projectId={project_id}
                config={bqConfig}
                selectedColumns={selectedColumns}
                imageColumn={imageColumn}
                promptRules={promptRules}
                treatmentName={treatmentName}
                onBack={handleBack}
                mediaType={mediaType}
                removeBackground={removeBackground}
              />
            ) : (
              <PatternPreviewPanel
                projectId={project_id}
                config={bqConfig}
                selectedColumns={selectedColumns}
                imageColumn={imageColumn}
                patternRules={patternRules}
                patternPreviews={patternPreviews}
                treatmentName={treatmentName}
                onBack={handleBack}
              />
            )
          )}
        </div>
      </main>
    </div>
  );
};

interface TreatmentModeSelectorProps {
  onModeSelected: (mode: TreatmentMode) => void;
}

const TreatmentModeSelector: React.FC<TreatmentModeSelectorProps> = ({ onModeSelected }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Choose Treatment Mode</h2>
        <p className="text-gray-400">Select how you want to process your images</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Prompt Mode */}
        <button
          onClick={() => onModeSelected('prompt')}
          className="group relative bg-gray-900/50 border border-gray-700 rounded-xl p-6 text-left hover:border-purple-500/50 hover:bg-gray-900/70 transition-all duration-300"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-500/20 rounded-lg group-hover:bg-purple-500/30 transition-colors">
              <Wand2 className="w-8 h-8 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">Prompt-Based</h3>
              <p className="text-sm text-gray-400 mb-4">
                Generate new images using text prompts. Define conditions and prompts that use your BQ column values as variables.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Simple text-to-image generation</li>
                <li>• Dynamic prompts with {'{column}'} variables</li>
                <li>• Optional background removal</li>
              </ul>
            </div>
          </div>
          <div className="absolute top-4 right-4 text-xs px-2 py-1 bg-gray-700 rounded text-gray-400">
            Recommended for simple edits
          </div>
        </button>

        {/* Pattern Mode */}
        <button
          onClick={() => onModeSelected('pattern')}
          className="group relative bg-gray-900/50 border border-gray-700 rounded-xl p-6 text-left hover:border-blue-500/50 hover:bg-gray-900/70 transition-all duration-300"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
              <GitBranch className="w-8 h-8 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">Pattern-Based</h3>
              <p className="text-sm text-gray-400 mb-4">
                Replay a sequence of operations from an existing thread. Customize prompts at each step using BQ column values.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Reuse complex multi-step workflows</li>
                <li>• Customize prompts per step with variables</li>
                <li>• Add reference images from BQ columns</li>
              </ul>
            </div>
          </div>
          <div className="absolute top-4 right-4 text-xs px-2 py-1 bg-blue-500/20 rounded text-blue-400">
            Advanced
          </div>
        </button>
      </div>
    </div>
  );
};

interface StepIndicatorProps {
  number: number;
  label: string;
  active: boolean;
  completed: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ number, label, active, completed }) => {
  return (
    <div className="flex items-center gap-2">
      <div 
        className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
          completed 
            ? 'bg-blue-500 text-white' 
            : active 
            ? 'bg-blue-500/20 text-blue-400 border-2 border-blue-500' 
            : 'bg-gray-700 text-gray-500'
        }`}
      >
        {completed ? '✓' : number}
      </div>
      <span className={`text-sm font-medium ${active ? 'text-white' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
};

export default BulkTreatment;
